class MediaGrid {
  constructor() {
    this.init();
  }

  init() {
    this.loadVimeoThumbnails();
    this.setupVideoPlayers();
    this.setupIntersectionObserver();
    this.setupMobileScroll();
  }

  loadVimeoThumbnails() {
    const vimeoThumbnails = document.querySelectorAll('.media-grid__video-thumbnail--vimeo');

    vimeoThumbnails.forEach(async (img) => {
      const vimeoId = img.dataset.vimeoId;
      if (!vimeoId) return;

      try {
        const response = await fetch(`https://vimeo.com/api/v2/video/${vimeoId}.json`);
        if (response.ok) {
          const data = await response.json();
          if (data && data[0] && data[0].thumbnail_large) {
            img.src = data[0].thumbnail_large;
          }
        } else {
          this.showVideoPlaceholder(img);
        }
      } catch (error) {
        console.warn('Failed to load Vimeo thumbnail:', error);
        this.showVideoPlaceholder(img);
      }
    });
  }

  showVideoPlaceholder(img) {
    const placeholder = document.createElement('div');
    placeholder.className = 'media-grid__video-placeholder';
    placeholder.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 5v14l11-7z" fill="currentColor"/>
      </svg>
    `;
    img.parentElement.replaceChild(placeholder, img);
  }

  setupVideoPlayers() {
    const playButtons = document.querySelectorAll('.media-grid__video-play');

    playButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const videoType = button.dataset.videoType;
        const videoId = button.dataset.videoId;
        const provider = button.dataset.videoProvider;

        if (videoType === 'hosted') {
          // Handle Shopify hosted video
          const videoElement = button.querySelector('.media-grid__video-source');
          if (videoElement) {
            this.openFullscreenVideo(videoElement.cloneNode(true));
          }
        } else if (videoId && provider) {
          // Handle external video (YouTube/Vimeo)
          this.openVideoModal(videoId, provider);
        }
      });
    });
  }

  openFullscreenVideo(videoElement) {
    // Create modal if it doesn't exist
    let modal = document.querySelector('.media-grid-video-modal');

    if (!modal) {
      modal = this.createVideoModal();
    }

    // Configure video for fullscreen playback
    videoElement.controls = true;
    videoElement.autoplay = true;
    videoElement.style.display = 'block';
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    videoElement.style.maxWidth = '100vw';
    videoElement.style.maxHeight = '100vh';
    videoElement.style.objectFit = 'contain';

    const iframeWrapper = modal.querySelector('.media-grid-video-modal__iframe-wrapper');
    iframeWrapper.innerHTML = '';
    iframeWrapper.appendChild(videoElement);

    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Play video
    videoElement.play().catch(err => {
      console.warn('Video autoplay failed:', err);
    });

    // Cleanup on close
    const closeHandler = () => {
      videoElement.pause();
      this.closeVideoModal();
    };

    modal.querySelector('.media-grid-video-modal__close').onclick = closeHandler;
    modal.onclick = (e) => {
      if (e.target === modal) {
        closeHandler();
      }
    };
  }

  openVideoModal(videoId, provider) {
    let modal = document.querySelector('.media-grid-video-modal');

    if (!modal) {
      modal = this.createVideoModal();
    }

    // Generate iframe based on provider
    let iframeSrc = '';
    if (provider === 'youtube') {
      iframeSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    } else if (provider === 'vimeo') {
      iframeSrc = `https://player.vimeo.com/video/${videoId}?autoplay=1&playsinline=1`;
    }

    const iframeWrapper = modal.querySelector('.media-grid-video-modal__iframe-wrapper');
    iframeWrapper.innerHTML = `
      <iframe
        src="${iframeSrc}"
        allow="autoplay; fullscreen; picture-in-picture; accelerometer; gyroscope"
        allowfullscreen
        style="width: 100%; height: 100%; border: none; max-width: 100vw; max-height: 100vh;"
      ></iframe>
    `;

    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  createVideoModal() {
    const modal = document.createElement('div');
    modal.className = 'media-grid-video-modal';
    modal.innerHTML = `
      <div class="media-grid-video-modal__content">
        <button class="media-grid-video-modal__close" aria-label="Close video">&times;</button>
        <div class="media-grid-video-modal__iframe-wrapper"></div>
      </div>
    `;
    document.body.appendChild(modal);

    // Close button functionality
    const closeBtn = modal.querySelector('.media-grid-video-modal__close');
    closeBtn.addEventListener('click', () => this.closeVideoModal());

    // Close on background click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeVideoModal();
      }
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        this.closeVideoModal();
      }
    });

    return modal;
  }

  closeVideoModal() {
    const modal = document.querySelector('.media-grid-video-modal');
    if (modal) {
      modal.classList.remove('active');
      const iframeWrapper = modal.querySelector('.media-grid-video-modal__iframe-wrapper');

      // Stop any playing video
      const video = iframeWrapper.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      // Clear content
      setTimeout(() => {
        iframeWrapper.innerHTML = '';
      }, 300); // Wait for fade out animation

      document.body.style.overflow = '';
    }
  }

  setupIntersectionObserver() {
    // Lazy load images as they come into viewport
    const images = document.querySelectorAll('.media-grid__image img, .media-grid__video img');

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;

            // If image has srcset, it's already loaded
            if (img.dataset.src) {
              img.src = img.dataset.src;
              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
              }
              img.removeAttribute('data-src');
              img.removeAttribute('data-srcset');
            }

            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      images.forEach(img => imageObserver.observe(img));
    }

    // Auto-play video previews when in viewport
    const videoPreviews = document.querySelectorAll('.media-grid__video-preview');
    if ('IntersectionObserver' in window && videoPreviews.length > 0) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {
              // Autoplay may be blocked, that's okay
            });
          } else {
            video.pause();
          }
        });
      }, {
        threshold: 0.5
      });

      videoPreviews.forEach(video => videoObserver.observe(video));
    }
  }

  setupMobileScroll() {
    const mobileScrollContainers = document.querySelectorAll('.media-grid--mobile-scroll');

    mobileScrollContainers.forEach(container => {
      let isDown = false;
      let startX;
      let scrollLeft;
      let velocity = 0;
      let lastX = 0;
      let lastTime = Date.now();

      // Touch events for mobile
      container.addEventListener('touchstart', (e) => {
        isDown = true;
        startX = e.touches[0].pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        velocity = 0;
        lastX = e.touches[0].pageX;
        lastTime = Date.now();
      }, { passive: true });

      container.addEventListener('touchmove', (e) => {
        if (!isDown) return;
        const x = e.touches[0].pageX - container.offsetLeft;
        const walk = (x - startX) * 1.5;
        container.scrollLeft = scrollLeft - walk;

        // Calculate velocity for momentum
        const currentTime = Date.now();
        const timeDiff = currentTime - lastTime;
        if (timeDiff > 0) {
          velocity = (e.touches[0].pageX - lastX) / timeDiff;
        }
        lastX = e.touches[0].pageX;
        lastTime = currentTime;
      }, { passive: true });

      container.addEventListener('touchend', () => {
        isDown = false;

        // Apply momentum scrolling
        if (Math.abs(velocity) > 0.1) {
          this.applyMomentum(container, velocity);
        }
      }, { passive: true });

      // Mouse events for desktop testing
      container.addEventListener('mousedown', (e) => {
        isDown = true;
        container.style.cursor = 'grabbing';
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
      });

      container.addEventListener('mouseleave', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mouseup', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 2;
        container.scrollLeft = scrollLeft - walk;
      });
    });
  }

  applyMomentum(container, velocity) {
    const friction = 0.95;
    const minVelocity = 0.1;

    const momentum = () => {
      velocity *= friction;

      if (Math.abs(velocity) > minVelocity) {
        container.scrollLeft -= velocity * 10;
        requestAnimationFrame(momentum);
      }
    };

    requestAnimationFrame(momentum);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MediaGrid();
  });
} else {
  new MediaGrid();
}

// Reinitialize on section re-render (Shopify theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new MediaGrid();
  });
}
